import { useMemo } from "react";
import type { z } from "zod";

import type { Prettify } from "@thalamos/common";
import type { WorkflowVersion } from "@thalamos/vision-common";
import {
  createInitialValues,
  getWorkflowTranslationContext,
  tk,
  workflows,
} from "@thalamos/vision-common";

import type { MultiPageFormProps } from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import {
  MultiPageForm,
  MultiPageFormSkeleton,
} from "../../components/FormBuilder/MultiPageForm/MultiPageForm.js";
import { Error } from "../../pages/Error/Error.js";

type RecordPlaceOfSafetySchema<T extends WorkflowVersion> = z.infer<
  ReturnType<
    (typeof workflows)[T]["recordPlaceOfSafety"]["formDefinition"]["sections"][0]["buildFormSpecification"]
  >["formSchema"]
>;

///

export function RecordPlaceOfSafetyFormSkeleton() {
  return <MultiPageFormSkeleton headerText={tk("recordPlaceOfSafety.title")} />;
}

///

export type RecordPlaceOfSafetyFormProps<T extends WorkflowVersion> = Prettify<
  {
    workflowVersion: T;
  } & Pick<
    MultiPageFormProps<RecordPlaceOfSafetySchema<T>>,
    | "initialValues"
    | "incidentWorkItem"
    | "onAutoSave"
    | "onSubmit"
    | "onCancel"
  >
>;

///

export function RecordPlaceOfSafetyForm<T extends WorkflowVersion>({
  workflowVersion,
  incidentWorkItem,
  initialValues,
  onAutoSave,
  onSubmit,
  onCancel,
}: RecordPlaceOfSafetyFormProps<T>) {
  const workflow = workflows[workflowVersion];
  const formSpecification =
    workflow?.recordPlaceOfSafety.formDefinition.sections[0]?.buildFormSpecification(
      { incidentWorkItem },
    );

  const initialValuesOrDefaults: Record<string, unknown> = useMemo(
    () =>
      formSpecification === undefined
        ? {}
        : createInitialValues(
            formSpecification.strictFieldSchemas,
            initialValues,
          ),
    [initialValues, formSpecification],
  );

  if (formSpecification === undefined) {
    return <Error />;
  }

  const formSchema = formSpecification.formSchema;
  const strictFieldSchemas = formSpecification.strictFieldSchemas;

  return (
    <MultiPageForm<typeof formSchema>
      headerText={({ incidentWorkItem }) => ({
        translationKey: tk("recordPlaceOfSafety.title"),
        translationOptions: {
          context: getWorkflowTranslationContext({ incidentWorkItem }),
        },
      })}
      incidentWorkItem={incidentWorkItem}
      schema={formSchema}
      strictFieldSchemas={strictFieldSchemas}
      initialValues={initialValuesOrDefaults}
      pages={formSpecification.pages}
      onCancel={onCancel}
      onSubmit={onSubmit}
      onAutoSave={onAutoSave}
    />
  );
}
